import { useCallback, type ReactNode } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { RichEditor, type TiptapDoc } from '@odo/shared-ui';
import type { VirtualAppointmentDto } from '../../services/virtualAppointments';
import { TeamPill } from '@odo/shared-ui/components/TeamPill';
import { FieldLabel } from './VirtualAppointmentFieldParts';
import {
    formatSummaryTrigger,
    fullTypeTitle
} from '@odo/shared-ui/clinical/virtualAppointments/summary';
import { summaryFromDto } from './virtualAppointmentSummary';
import { VirtualAppointmentTypeTitle } from '@odo/shared-ui/clinical/virtualAppointments/VirtualAppointmentTypeTitle';
import { VirtualAppointmentAssigneeValue } from './VirtualAppointmentAssignee';

const noopUpload = (): Promise<{ url: string; fileId: string }> =>
    Promise.resolve({ url: '', fileId: '' });

interface DescriptionBoxProps {
    children: ReactNode;
}

// Bordered box that takes the panel's remaining height; the editor inside scrolls.
export function VirtualAppointmentDescriptionBox({ children }: DescriptionBoxProps) {
    return (
        <div className="flex-1 min-h-[160px] flex flex-col rounded-lg border border-gray-200 overflow-hidden">
            {children}
        </div>
    );
}

interface ReadOnlyFieldProps {
    label: string;
    children: ReactNode;
}

function ReadOnlyField({ label, children }: ReadOnlyFieldProps) {
    return (
        <div className="flex flex-col">
            <FieldLabel>{label}</FieldLabel>
            <div className="flex items-center h-9 px-3 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-500 cursor-not-allowed">
                {children}
            </div>
        </div>
    );
}

type DescriptionLayout = 'fill' | 'plain';

interface ReadOnlyDescriptionProps {
    doc: TiptapDoc | null;
    /** 'fill' is a labelled box taking the panel's remaining height; 'plain' is the bare text. */
    layout?: DescriptionLayout;
}

export function VirtualAppointmentReadOnlyDescription({
    doc,
    layout = 'fill'
}: ReadOnlyDescriptionProps) {
    const noopChange = useCallback((_doc: TiptapDoc) => undefined, []);
    const fill = layout === 'fill';
    const editor = doc && (
        <RichEditor
            value={doc}
            onChange={noopChange}
            onImageUpload={noopUpload}
            readOnly
            chrome={fill ? 'flush' : 'bare'}
        />
    );
    return (
        <div className={fill ? 'flex-1 min-h-0 flex flex-col' : 'flex flex-col'}>
            {fill && <FieldLabel>Description</FieldLabel>}
            {!editor && (
                <span className={`${fill ? 'text-sm' : 'text-[13px]'} text-gray-400 italic`}>
                    No description
                </span>
            )}
            {editor && fill && (
                <VirtualAppointmentDescriptionBox>{editor}</VirtualAppointmentDescriptionBox>
            )}
            {/* Cancels the editor's padding to line up with the row, and sizes the prose to the rows' 13px text. */}
            {editor && !fill && (
                <div className="-mx-3 -my-2.5 [&_.prose]:text-[13px] [&_.prose]:leading-relaxed">
                    {editor}
                </div>
            )}
        </div>
    );
}

interface ReadOnlyTriggerProps {
    va: VirtualAppointmentDto;
}

function ReadOnlyTrigger({ va }: ReadOnlyTriggerProps) {
    return (
        <ReadOnlyField label="Trigger">
            <span className="truncate">{formatSummaryTrigger(summaryFromDto(va))}</span>
        </ReadOnlyField>
    );
}

function ReadOnlyAssignee({ va }: ReadOnlyTriggerProps) {
    return (
        <ReadOnlyField label="Assignee">
            <VirtualAppointmentAssigneeValue team={va.team} assigneeEmail={va.assigneeEmail} />
        </ReadOnlyField>
    );
}

interface VirtualAppointmentReadOnlyDetailProps {
    va: VirtualAppointmentDto;
    /** Puts the type up as a title with this beside it (e.g. a copy button); team and trigger share a row. */
    titleAction?: ReactNode;
}

interface TitledReadOnlyDetailProps {
    va: VirtualAppointmentDto;
    action: ReactNode;
}

function TitledReadOnlyDetail({ va, action }: TitledReadOnlyDetailProps) {
    return (
        <div className="flex-1 min-h-0 flex flex-col gap-4 px-5 py-4 overflow-y-auto">
            <div className="flex items-center justify-between gap-3">
                <h3 className="min-w-0 text-lg font-bold text-gray-800">
                    <VirtualAppointmentTypeTitle summary={summaryFromDto(va)} />
                </h3>
                <div className="flex-shrink-0">{action}</div>
            </div>
            <div className="grid grid-cols-3 gap-3">
                <ReadOnlyField label="Team">
                    <TeamPill team={va.team} size="xs" />
                </ReadOnlyField>
                <ReadOnlyTrigger va={va} />
                <ReadOnlyAssignee va={va} />
            </div>
            <VirtualAppointmentReadOnlyDescription doc={va.descriptionDoc} />
        </div>
    );
}

// A VA past 'planned' is being worked: same fields as the editor, all read-only.
export function VirtualAppointmentReadOnlyDetail({
    va,
    titleAction
}: VirtualAppointmentReadOnlyDetailProps) {
    if (titleAction) return <TitledReadOnlyDetail va={va} action={titleAction} />;
    const summary = summaryFromDto(va);
    return (
        <div className="flex-1 min-h-0 flex flex-col gap-4 px-5 py-4 overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
                <ReadOnlyField label="Type">
                    <span className="truncate" title={fullTypeTitle(summary)}>
                        {fullTypeTitle(summary, ' - ')}
                    </span>
                </ReadOnlyField>
                <ReadOnlyTrigger va={va} />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <ReadOnlyField label="Team">
                    <TeamPill team={va.team} size="xs" />
                </ReadOnlyField>
                <ReadOnlyAssignee va={va} />
            </div>
            <VirtualAppointmentReadOnlyDescription doc={va.descriptionDoc} />
        </div>
    );
}

interface VirtualAppointmentDetailPromptProps {
    message: string;
}

export function VirtualAppointmentDetailPrompt({ message }: VirtualAppointmentDetailPromptProps) {
    return (
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-2 p-10 text-center">
            <CalendarDaysIcon className="w-6 h-6 text-gray-400" />
            <p className="text-sm text-gray-500">{message}</p>
        </div>
    );
}
